import { useState } from 'react';
import type { CollectionDef } from './collections';
import type { CreateVocabBody } from './settings-api';
import { canWrite, disabledReason, type WriteGate } from './WriteGate';

/**
 * 🔴 THE ONLY PLACE A `code` CAN BE TYPED.
 *
 * Creation is the one moment the value is not yet referenced by a ticket, by
 * `ticket_status_history`, or by a `status_transition` row — so it is the one moment it can be
 * chosen. Afterwards it is load-bearing history and the row editor has no field for it at all.
 *
 * ⚠️ THE WARNING IS ON THE FORM, NOT IN A TOOLTIP. "You will not be able to change this" is
 * information the person needs while typing it, not after they have got it wrong.
 *
 * ⭐ The suggested code is derived from the label and then left ALONE once touched: auto-syncing it
 * forever would rewrite a deliberate code the moment somebody fixed a typo in the label.
 */
export function AddRowForm({
  def,
  gate,
  lanes,
  busy,
  error,
  onCreate,
}: {
  def: CollectionDef;
  gate: WriteGate;
  /** Statuses need a lane; every other collection passes an empty list and the field is absent. */
  lanes: readonly { code: string; label: string }[];
  busy: boolean;
  error: string | null;
  onCreate: (body: CreateVocabBody) => void;
}) {
  const editable = canWrite(gate);
  const why = disabledReason(gate);

  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [code, setCode] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);
  const [colour, setColour] = useState('');
  const [lane, setLane] = useState(lanes[0]?.code ?? '');

  const effectiveCode = codeTouched ? code : suggestCode(label);
  const valid = label.trim().length > 0 && /^[a-z0-9_]{2,64}$/.test(effectiveCode);

  if (!open) {
    return (
      <div className="border-t border-[color:var(--color-line)] px-3 py-3 sm:px-4">
        <button
          type="button"
          disabled={!editable}
          title={why}
          onClick={() => setOpen(true)}
          className="h-9 rounded border border-dashed border-[color:var(--color-line)] px-3 text-sm font-semibold disabled:opacity-50"
        >
          + Add {def.singular}
        </button>
      </div>
    );
  }

  return (
    <form
      className="space-y-3 border-t border-[color:var(--color-line)] bg-[color:var(--color-surface-sunken)] px-3 py-3 sm:px-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid || !editable) return;
        onCreate({
          code: effectiveCode,
          label: label.trim(),
          ...(def.hasColour ? { colour: colour || null } : {}),
          ...(lanes.length > 0 ? { lane } : {}),
        });
      }}
    >
      <h3 className="text-sm font-semibold">New {def.singular}</h3>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-semibold" htmlFor={`new-label-${def.name}`}>
            Label
          </label>
          <input
            id={`new-label-${def.name}`}
            value={label}
            disabled={!editable || busy}
            onChange={(event) => setLabel(event.target.value)}
            className="mt-1 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-1.5 text-sm"
          />
          <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
            Shown everywhere. Freely editable later.
          </p>
        </div>

        <div>
          <label className="block text-xs font-semibold" htmlFor={`new-code-${def.name}`}>
            Code
          </label>
          <input
            id={`new-code-${def.name}`}
            value={effectiveCode}
            disabled={!editable || busy}
            onChange={(event) => {
              setCodeTouched(true);
              setCode(event.target.value.trim().toLowerCase());
            }}
            className="mt-1 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-1.5 font-mono text-sm"
          />
          <p className="mt-1 text-xs font-semibold text-amber-700 dark:text-amber-300">
            🔴 Permanent. Tickets and history reference this value, so it can never be changed — a
            rename would re-point every historical row at a different meaning.
          </p>
        </div>

        {lanes.length > 0 ? (
          <div>
            <label className="block text-xs font-semibold" htmlFor={`new-lane-${def.name}`}>
              Lane
            </label>
            <select
              id={`new-lane-${def.name}`}
              value={lane}
              disabled={!editable || busy}
              onChange={(event) => setLane(event.target.value)}
              className="mt-1 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-1.5 text-sm"
            >
              {lanes.map((entry) => (
                <option key={entry.code} value={entry.code}>
                  {entry.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {def.hasColour ? (
          <div>
            <label className="block text-xs font-semibold" htmlFor={`new-colour-${def.name}`}>
              Colour
            </label>
            <input
              id={`new-colour-${def.name}`}
              value={colour}
              placeholder="#rrggbb — optional"
              disabled={!editable || busy}
              onChange={(event) => setColour(event.target.value)}
              className="mt-1 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-1.5 font-mono text-sm"
            />
          </div>
        ) : null}
      </div>

      {!valid && label.trim() ? (
        <p className="text-xs text-[color:var(--color-ink-muted)]">
          A code is lower-case letters, digits and underscores, 2–64 characters.
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="text-xs font-semibold text-red-700 dark:text-red-300">
          {error}
        </p>
      ) : null}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!editable || busy || !valid}
          title={why}
          className="h-9 rounded bg-[color:var(--color-accent)] px-3 text-sm font-semibold text-[color:var(--color-on-accent)] disabled:opacity-50"
        >
          Create {def.singular}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="h-9 rounded border border-[color:var(--color-line)] px-3 text-sm"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

/** `Code Review` → `code_review`. A suggestion only — see the note on `codeTouched`. */
export function suggestCode(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
}
